import CheckIcon from "@mui/icons-material/Check";
import { Checkbox, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import parse from "html-react-parser";
import React, { Fragment } from "react";
import Clock from "../clock/clock";
import { maxLines } from "../constants";
import { MEDIA_QUERIES, getResponsiveValue } from "../responsive/mediaQueries";
import "./itemContent.css";

// Convert to proper React component with uppercase first letter
export const ItemContent = ({ item, data, header, filterStore, videoFile, onVideoClick, onCheckboxChange, currentView, userStore, precision, onJumpToItem }) => {
    // Use media query hooks to detect screen size using our constants
    const isXSmallScreen = useMediaQuery(MEDIA_QUERIES.xs);
    const isSmallScreen = useMediaQuery(MEDIA_QUERIES.sm);
    const isMediumScreen = useMediaQuery(MEDIA_QUERIES.md);
    const isLargeScreen = useMediaQuery(MEDIA_QUERIES.lg);

    // Function to determine if a column should be visible based on screen size
    const shouldShowColumn = (columnField) => {
        // Priority columns that should always be visible
        const alwaysVisibleColumns = ['', 'order', 'play', 'sentence', 'speaker', 'sentiment'];

        // Secondary columns that should be visible on medium screens and larger
        const mediumScreenColumns = [...alwaysVisibleColumns, 'length', 'sec', 'question'];

        // Columns visible only on large screens
        const smallScreenColumns = [...alwaysVisibleColumns, 'length'];

        if (isXSmallScreen) {
            return alwaysVisibleColumns.includes(columnField);
        } else if (isSmallScreen) {
            return smallScreenColumns.includes(columnField);
        } else if (isMediumScreen) {
            return mediumScreenColumns.includes(columnField);
        }

        // On large screens, respect the visibility setting from the header
        return true;
    };

    // Responsive styling adjustments using our utility function
    const getResponsiveStyles = (obj) => {
        // Base styles that apply to all fields
        const baseStyles = {
            width: getResponsiveValue(
                isXSmallScreen,
                isSmallScreen,
                isMediumScreen,
                isLargeScreen,
                {
                    xs: 'auto',
                    sm: 'auto',
                    md: 'auto',
                    default: obj.width
                },
                obj.width
            ),
            maxWidth: getResponsiveValue(
                isXSmallScreen,
                isSmallScreen,
                isMediumScreen,
                isLargeScreen,
                {
                    xs: 'none',
                    sm: 'none',
                    md: 'none',
                    default: obj.width
                },
                obj.width
            ),
            padding: getResponsiveValue(
                isXSmallScreen,
                isSmallScreen,
                isMediumScreen,
                isLargeScreen,
                {
                    xs: '2px',
                    sm: '2px',
                    md: '3px',
                    default: '4px'
                },
                '4px'
            ),
            textAlign: obj.align || 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        };

        // Special override for Clock component column
        if (obj.type === 'icon' && obj.field === 'duration') {
            return {
                ...baseStyles,
                padding: 0, // Remove padding from TD
                textAlign: 'center', // Center align TD content
                verticalAlign: 'middle', // Align vertically like header
            };
        }

        // Special case for sentence field - make it responsive to video width
        if (obj.field === 'sentence') {
            const dynamicVideoWidth = filterStore.getVideoWidth(currentView);

            // Calculate sentence width based on video width
            // When video is smaller (30px), sentence gets more space
            // When video is larger (150px), sentence gets less space
            let calculatedSentenceWidth;

            if (isXSmallScreen) {
                calculatedSentenceWidth = '100%';
            } else if (isSmallScreen) {
                // On small screens, adjust based on video width
                calculatedSentenceWidth = dynamicVideoWidth <= 50 ? '200px' : '150px';
            } else if (isMediumScreen) {
                // On medium screens, more responsive to video width
                calculatedSentenceWidth = dynamicVideoWidth <= 50 ? '350px' :
                    dynamicVideoWidth <= 100 ? '300px' : '250px';
            } else {
                // On large screens, use specific mapping:
                // 30px video → 300px sentence
                // 90px video → 280px sentence  
                // 150px video → 250px sentence
                let sentenceWidth;
                if (dynamicVideoWidth <= 30) {
                    sentenceWidth = 300;
                } else if (dynamicVideoWidth <= 90) {
                    // Linear interpolation between 30px→300px and 90px→280px
                    const ratio = (dynamicVideoWidth - 30) / (90 - 30);
                    sentenceWidth = 300 + (280 - 300) * ratio;
                } else {
                    // Linear interpolation between 90px→280px and 150px→250px
                    const ratio = (dynamicVideoWidth - 90) / (150 - 90);
                    sentenceWidth = 280 + (250 - 280) * ratio;
                }
                calculatedSentenceWidth = `${Math.round(sentenceWidth)}px`;
            }

            return {
                ...baseStyles,
                width: calculatedSentenceWidth,
                maxWidth: calculatedSentenceWidth,
                minWidth: isXSmallScreen ? '100px' : isSmallScreen ? '150px' : '250px',
                flex: isXSmallScreen || isSmallScreen ? 1 : 'none',
                // Add extra left padding to create spacing between video and sentence
                padding: getResponsiveValue(
                    isXSmallScreen,
                    isSmallScreen,
                    isMediumScreen,
                    isLargeScreen,
                    {
                        xs: '2px 2px 2px 12px',    // Extra left spacing on mobile
                        sm: '2px 2px 2px 15px',    // More left spacing on small screens
                        md: '3px 3px 3px 18px',    // Even more left spacing on medium screens
                        default: '4px 4px 4px 20px' // Maximum left spacing on large screens
                    },
                    '4px 4px 4px 20px'
                )
            };
        }

        // Adjust video cell width on different screens
        if (obj.type === 'video') {
            const dynamicVideoWidth = filterStore.getVideoWidth(currentView);

            // Apply responsive constraints while respecting user's video width setting
            const getConstrainedWidth = (screenSize) => {
                let maxAllowed, minAllowed;

                if (isXSmallScreen) {
                    minAllowed = 70; maxAllowed = 130; // Mobile range
                } else if (isSmallScreen) {
                    minAllowed = 20; maxAllowed = 250; // Tablet range  
                } else {
                    minAllowed = 30; maxAllowed = 400; // Desktop range
                }

                // Constrain user's preference within screen-appropriate limits
                const constrainedWidth = Math.max(minAllowed, Math.min(dynamicVideoWidth, maxAllowed));
                return `${constrainedWidth}px`;
            };

            const responsiveWidth = getConstrainedWidth();

            return {
                ...baseStyles,
                width: responsiveWidth,
                maxWidth: responsiveWidth,
                minWidth: responsiveWidth,
                // Add extra right padding to create spacing between video and sentence
                padding: getResponsiveValue(
                    isXSmallScreen,
                    isSmallScreen,
                    isMediumScreen,
                    isLargeScreen,
                    {
                        xs: '2px 8px 2px 2px',    // Extra spacing on mobile
                        sm: '2px 10px 2px 2px',   // More spacing on small screens
                        md: '2px 12px 2px 2px',   // Even more spacing on medium screens
                        default: '2px 15px 2px 2px' // Maximum spacing on large screens
                    },
                    '2px 15px 2px 2px'
                ),
                // Prevent the column from expanding beyond the set width
                boxSizing: 'border-box',
                overflow: 'hidden',
            };
        }

        return baseStyles;
    };


    return (
        <Fragment>
            {header.map((obj, index) => {
                // Only render visible columns and apply responsive filtering
                if (obj.visible && shouldShowColumn(obj.field)) {
                    if (obj.type === 'checkbox') {
                        return (
                            <td key={index} className={`${obj.field} checkbox`} style={{ ...getResponsiveStyles(obj) }}>
                                <Stack direction="row" spacing={0} alignItems={"center"} justifyContent="left">
                                    {!isXSmallScreen && <Typography style={{ marginLeft: 2, fontSize: 11 }}>{item + 1}</Typography>}
                                    <Checkbox
                                        sx={{ '& .MuiSvgIcon-root': { fontSize: isSmallScreen ? 14 : 15 } }}
                                        value={data['id']}
                                        checked={filterStore.isSelectedSentence(data['id'], currentView)}
                                        onChange={(event) => onCheckboxChange(event, item, data['id'])}
                                    />
                                </Stack>

                            </td>
                        )
                    }
                    if (obj.type === 'listCategory') {
                        // Check if the field exists and is an array before accessing length
                        if (data[obj.field] && Array.isArray(data[obj.field]) && data[obj.field].length > 0) {
                            return (
                                <td key={index} className={`${obj.field} listCategory`} style={getResponsiveStyles(obj)}>
                                    {data[obj.field].map((item) => {
                                        return <p key={item['id']} style={{ margin: '2px 0' }}>{item['category']}</p>
                                    })}
                                </td>
                            )
                        } else {
                            // Return empty cell if no data or empty array
                            return (
                                <td key={index} className={`${obj.field} listCategory`} style={getResponsiveStyles(obj)}>
                                </td>
                            )
                        }
                    }
                    if (obj.type === 'video') {
                        return (
                            <td key={index} className={`${obj.field} video`} style={getResponsiveStyles(obj)}>
                                <video id={`v-${item}`}
                                    playsInline
                                    className="videos"
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block'
                                    }}
                                    src={videoFile} type="video/mp4"
                                    preload="none"
                                    poster={data['thumbnail']}
                                    onClick={(event) => {
                                        onVideoClick(event, data, item)
                                    }}
                                />
                            </td>
                        )
                    }

                    if (obj.type === 'boolean') {
                        return (
                            <td key={index} className={`${obj.field} boolean`} style={getResponsiveStyles(obj)}>
                                {(data[obj.field] ? <CheckIcon color="success" /> : null)}
                            </td>
                        )
                    }
                    if (obj.field === 'sentence') {
                        let search;
                        if (filterStore.getSearch(currentView).includes(' ')) {
                            search = filterStore.getSearch(currentView).split(' ').filter(word => word !== '');
                        } else {
                            search = [filterStore.getSearch(currentView)];
                        }
                        const words = data[obj.field].split(' ');
                        let html = words.map((word, index) => {
                            return `<span id="s-${item}-${index}">${word}</span>`;
                        });

                        // Restore search highlighting functionality
                        for (let i = 0; i < words.length; i++) {
                            for (let j = 0; j < search.length; j++) {
                                const word = words[i];
                                const wordSearch = words[i].toLowerCase();
                                // exact match
                                if (wordSearch === search[j]) {
                                    html[i] = `<span id="s-${item}-${i}" class="highlight">${word}</span>`;
                                }
                                if (search[j] !== '') {
                                    // starts with
                                    if (wordSearch.startsWith(search[j])) {
                                        html[i] = `<span id="s-${item}-${i}" class="highlight">${word.slice(0, search[j].length)}</span>${word.slice(search[j].length)}`;
                                    }
                                }
                            }
                        }

                        // No truncation - show full text with karaoke functionality intact
                        const htmlJoined = html.join(' ');

                        return (
                            <td key={index} className={`${obj.field} ${obj.type}`} style={getResponsiveStyles(obj)}>
                                <div style={{
                                    fontSize: isXSmallScreen ? '12px' : isSmallScreen ? '14px' : '16px',
                                    lineHeight: isXSmallScreen ? '1.2' : '1.4'
                                }}>
                                    {parse(htmlJoined)}
                                </div>
                            </td>
                        )
                    }
                    if (obj.field === 'sec') {
                        return (
                            <td key={index} className={`${obj.field} ${obj.type}`} style={getResponsiveStyles(obj)}>
                                {precision ? data[obj.field] : data['sec_int']}
                            </td>
                        )
                    }
                    if (obj.field === 'order') {
                        return (
                            <td key={index} className={`${obj.field} ${obj.type}`} style={getResponsiveStyles(obj)}>
                                <Stack direction="row" spacing={0} alignItems={"center"} justifyContent="center">
                                    {data[obj.field] === maxLines ? '' : data[obj.field]}
                                </Stack>
                            </td>
                        )
                    }
                    if (obj.field === 'sentiment') {
                        // Map sentiment values to compact symbols
                        const sentimentSymbols = {
                            'positive': '+',
                            'neutral': '=',
                            'negative': '-'
                        };

                        // Map sentiment values to tooltip text
                        const sentimentTooltips = {
                            'positive': 'Positive sentiment',
                            'neutral': 'Neutral sentiment',
                            'negative': 'Negative sentiment'
                        };

                        return (
                            <td key={index} className={`${data[obj.field]} ${obj.type}`} style={getResponsiveStyles(obj)}>
                                <Stack direction="row" spacing={0} alignItems={"center"} justifyContent="center">
                                    <Tooltip
                                        title={sentimentTooltips[data[obj.field]] || data[obj.field]}
                                        placement="top"
                                        arrow
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontSize: '32px',
                                                fontWeight: 'bold',
                                                lineHeight: 1,
                                                cursor: 'default'
                                            }}
                                        >
                                            {sentimentSymbols[data[obj.field]] || data[obj.field]}
                                        </Typography>
                                    </Tooltip>
                                </Stack>
                            </td>
                        )
                    }
                    if (obj.field === 'speaker') {
                        return (
                            <td key={index} className={`${data[obj.field]} ${obj.type}`} style={getResponsiveStyles(obj)}>
                                <Stack direction="row" spacing={0} alignItems={"center"} justifyContent="center">
                                    {data[obj.field]}
                                </Stack>
                            </td>
                        )
                    }
                    if (obj.field === 'duration') {
                        return (
                            <td key={index} className={`${obj.field} ${obj.type}`} style={getResponsiveStyles(obj)}>
                                <Clock
                                    header={true}
                                    duration={precision ? data['duration'] : data['duration'].map(item => item.split('.')[0])}
                                    item={data}
                                    onJumpClick={onJumpToItem}
                                    hasSearch={filterStore.getSearch(currentView).length > 0}
                                />

                            </td>
                        )
                    } else if (obj.field === 'entity_type' || obj.field === 'label') {

                        return (
                            <td key={index} className={`${obj.field} ${obj.type}`} style={getResponsiveStyles(obj)}>
                                {data[obj.field] !== undefined ? data[obj.field].charAt(0).toUpperCase() + data[obj.field].slice(1).replace(/_/g, ' ') : ''}
                            </td>
                        )
                    } else {
                        // For generic field types
                        // Determine max text length based on screen size
                        const maxTextLength = getResponsiveValue(
                            isXSmallScreen,
                            isSmallScreen,
                            isMediumScreen,
                            isLargeScreen,
                            {
                                xs: 15,
                                sm: 20,
                                md: 30,
                                default: 100
                            },
                            100
                        );

                        return (
                            <td key={index} className={`${obj.field} ${obj.type}`} style={getResponsiveStyles(obj)}>
                                {(isSmallScreen || isXSmallScreen) &&
                                    typeof data[obj.field] === 'string' &&
                                    data[obj.field].length > maxTextLength
                                    ? data[obj.field].substring(0, maxTextLength) + '...'
                                    : data[obj.field]}
                            </td>
                        )
                    }
                }
                return null;
            })}
        </Fragment>
    );
};

